// =====================================================================
// WeddingOS – /guest/photos (UPDATED)
// Neu:
//   • Ordner-Auswahl beim Upload (wenn Ordner vorhanden)
//   • Download-Button auf jedem Foto
//   • Filter nach Ordner in der Galerie
// =====================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchPhotos, uploadPhoto, realtime } from "../../services/supabase";
import { supabase } from "../../services/supabase";
import { loadGuestSession } from "../../lib/guestSession";
import InfoTip from "../../components/InfoTip";
import "../../styles/theme.css";

export default function GuestPhotos() {
  const navigate = useNavigate();
  const session  = loadGuestSession();

  const [photos,    setPhotos]    = useState([]);
  const [albums,    setAlbums]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [preview,   setPreview]   = useState(null);
  const [comment,   setComment]   = useState("");
  const [albumId,   setAlbumId]   = useState("");   // ausgewählter Ordner beim Upload
  const [albumFilter, setAlbumFilter] = useState("all"); // Galerie-Filter
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const [done,      setDone]      = useState(false);
  const [batch,     setBatch]     = useState(null); // { total, done } während Mehrfach-Upload
  const camRef = useRef(null); // Kamera (capture)
  const galRef = useRef(null); // Galerie/Dateien

  // ── Laden ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!session?.wedding_id) return;
    Promise.all([
      fetchPhotos(session.wedding_id),
      supabase.from("photo_albums")
        .select("id, name, sort_order")
        .eq("wedding_id", session.wedding_id)
        .order("sort_order"),
    ]).then(([photos, { data: albums }]) => {
      setPhotos(photos ?? []);
      setAlbums(albums ?? []);
    }).finally(() => setLoading(false));
  }, [session?.wedding_id]);

  // Realtime
  useEffect(() => {
    if (!session?.wedding_id) return;
    const unsub = realtime.photos(session.wedding_id, (payload) => {
      if (payload.eventType === "INSERT") {
        setPhotos((prev) => prev.some((p) => p.id === payload.new.id) ? prev : [payload.new, ...prev]);
      }
    });
    return unsub;
  }, [session?.wedding_id]);

  // ── Mehrere Fotos direkt hochladen (ohne Einzel-Vorschau) ─────────
  const handleBatchUpload = useCallback(async (files) => {
    if (!session?.guest_id) { setError("Sitzung ungültig – bitte Einladungslink neu öffnen."); return; }
    setError(null);
    setBatch({ total: files.length, done: 0 });
    for (const file of files) {
      try {
        const photo = await uploadPhoto({ guestId: session.guest_id, weddingId: session.wedding_id, file, comment: "", albumId: null });
        setPhotos((prev) => prev.some((p) => p.id === photo.id) ? prev : [photo, ...prev]);
      } catch (e) {
        if (String(e?.message || e).includes("photo_limit_reached")) {
          setError("Galerie voll – im Gratis-Paket sind 50 Fotos möglich. Das Brautpaar kann mit Premium unbegrenzt freischalten.");
          break;
        }
      }
      setBatch((b) => b ? { ...b, done: b.done + 1 } : b);
    }
    setBatch(null);
    setDone(true); setTimeout(() => setDone(false), 4000);
  }, [session]);

  // ── File-Handling ─────────────────────────────────────────────────
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setError(null);
    // Ein Foto → Vorschau mit Kommentar/Ordner. Mehrere → direkt hochladen.
    if (files.length === 1) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (ev) => setPreview({ file, dataUrl: ev.target.result });
      reader.readAsDataURL(file);
      return;
    }
    handleBatchUpload(files);
  }, [handleBatchUpload]);

  const handleUpload = useCallback(async () => {
    if (!preview) return;
    if (!session?.guest_id) { setError("Sitzung ungültig – bitte Einladungslink neu öffnen."); return; }
    setUploading(true); setError(null);
    try {
      const photo = await uploadPhoto({
        guestId:   session.guest_id,
        weddingId: session.wedding_id,
        file:      preview.file,
        comment,
        albumId:   albumId || null,
      });
      setPhotos((prev) => prev.some((p) => p.id === photo.id) ? prev : [photo, ...prev]);
      setPreview(null); setComment(""); setAlbumId("");
      setDone(true); setTimeout(() => setDone(false), 4000);
    } catch (e) {
      const msg = String(e?.message || e);
      setError(msg.includes("photo_limit_reached")
        ? "Die Galerie ist voll – im Gratis-Paket sind 50 Fotos möglich. Das Brautpaar kann mit Premium unbegrenzt freischalten."
        : "Upload fehlgeschlagen. Bitte nochmal versuchen.");
    } finally { setUploading(false); }
  }, [preview, session, comment, albumId]);

  if (!session) return <NoSession />;

  // Gefilterte Fotos für Galerie
  const shownPhotos = photos.filter((p) =>
    albumFilter === "all" ? true : p.album_id === albumFilter
  );

  return (
    <div className="wos-shell">
      <PageHeader onBack={() => navigate("/guest")} title="Fotos" />

      {!preview && !done && !batch && (
        <InfoTip
          short="📸 Halte deine schönsten Momente fest – sofort für alle sichtbar."
          steps={[
            { title: "Foto wählen",    detail: "Kamera öffnen oder aus Galerie wählen." },
            { title: "Ordner wählen",  detail: "Optional einen Ordner auswählen." },
            { title: "Kommentar",      detail: "Optional einen kurzen Text ergänzen." },
            { title: "Teilen",         detail: "Erscheint sofort in der gemeinsamen Galerie." },
          ]}
        />
      )}

      {done && <SuccessBanner message="Foto hochgeladen! ✦" />}
      {error && <div className="wos-error">{error}</div>}

      {preview ? (
        <UploadForm
          preview={preview}
          comment={comment}
          onCommentChange={setComment}
          albums={albums}
          albumId={albumId}
          onAlbumChange={setAlbumId}
          onUpload={handleUpload}
          onCancel={() => { setPreview(null); setComment(""); setAlbumId(""); setError(null); }}
          uploading={uploading}
        />
      ) : batch ? (
        <BatchProgress batch={batch} />
      ) : (
        <UploadTrigger camRef={camRef} galRef={galRef} onFileChange={handleFileChange} />
      )}

      {/* ── Galerie ── */}
      {!loading && photos.length > 0 && (
        <div className="wos-rise" style={{ marginTop: 32 }}>
          {/* Album-Filter Tabs */}
          {albums.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
              <AlbumTab active={albumFilter === "all"} onClick={() => setAlbumFilter("all")}>
                Alle ({photos.length})
              </AlbumTab>
              {albums.map((a) => (
                <AlbumTab key={a.id} active={albumFilter === a.id} onClick={() => setAlbumFilter(a.id)}>
                  📁 {a.name} ({photos.filter(p => p.album_id === a.id).length})
                </AlbumTab>
              ))}
            </div>
          )}

          <div className="wos-divider" style={{ marginBottom: 16 }}>
            <span style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--ink-subtle)", whiteSpace: "nowrap" }}>
              {albumFilter === "all" ? "ALLE FOTOS" : (albums.find(a => a.id === albumFilter)?.name ?? "").toUpperCase()}
            </span>
          </div>

          {shownPhotos.length === 0 ? (
            <p style={{ textAlign: "center", color: "var(--ink-subtle)", fontSize: 14, padding: "24px 0" }}>
              Keine Fotos in diesem Ordner.
            </p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {shownPhotos.map((p, i) => (
                <GalerieItem key={p.id} photo={p} priority={i < 4} />
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <GalerieSkeleton />}
    </div>
  );
}

// ── UploadForm ────────────────────────────────────────────────────────
function UploadForm({ preview, comment, onCommentChange, albums, albumId, onAlbumChange, onUpload, onCancel, uploading }) {
  return (
    <div className="wos-rise">
      <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", marginBottom: 16, aspectRatio: "4/3", background: "var(--surface)" }}>
        <img src={preview.dataUrl} alt="Vorschau" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      </div>

      {/* Ordner auswählen */}
      {albums.length > 0 && (
        <div className="wos-field">
          <label className="wos-label" htmlFor="photo-album">
            Ordner <span style={{ color: "var(--ink-subtle)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
          </label>
          <select
            id="photo-album"
            className="wos-input"
            value={albumId}
            onChange={(e) => onAlbumChange(e.target.value)}
          >
            <option value="">Kein Ordner</option>
            {albums.map((a) => (
              <option key={a.id} value={a.id}>📁 {a.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="wos-field">
        <label className="wos-label" htmlFor="photo-comment">
          Kommentar <span style={{ color: "var(--ink-subtle)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
        </label>
        <textarea id="photo-comment" className="wos-textarea" rows={2}
          placeholder="Beschreibe den Moment …" value={comment}
          onChange={(e) => onCommentChange(e.target.value)} maxLength={200}
          style={{ resize: "none" }} />
      </div>

      <button className="wos-btn wos-btn-primary" onClick={onUpload} disabled={uploading} style={{ marginBottom: 10 }}>
        {uploading ? <><span className="wos-spinner" />Wird hochgeladen …</> : "Foto teilen ✦"}
      </button>
      <button className="wos-btn wos-btn-ghost" onClick={onCancel} disabled={uploading}>Anderes Foto</button>
    </div>
  );
}

// ── GalerieItem ───────────────────────────────────────────────────────
function GalerieItem({ photo, priority }) {
  const [expanded, setExpanded] = useState(false);

  const handleDownload = async (e) => {
    e.stopPropagation();
    try {
      const res  = await fetch(photo.url);
      const blob = await res.blob();
      const ext  = blob.type.split("/")[1] || "jpg";
      const a    = document.createElement("a");
      a.href     = URL.createObjectURL(blob);
      a.download = `hochzeit-${photo.id.slice(0, 8)}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { window.open(photo.url, "_blank"); }
  };

  return (
    <>
      <div style={{ position: "relative", borderRadius: "var(--radius-md)", overflow: "hidden", aspectRatio: "1", background: "var(--surface)" }}>
        <button onClick={() => setExpanded(true)} style={{ display: "block", width: "100%", height: "100%", padding: 0, border: "none", cursor: "pointer", background: "none" }}>
          <img src={photo.url} alt={photo.comment || "Hochzeitsfoto"}
            loading={priority ? "eager" : "lazy"}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        </button>

        {/* Download-Button über dem Foto */}
        <button
          onClick={handleDownload}
          title="Foto herunterladen"
          style={{
            position:      "absolute",
            bottom:        6,
            right:         6,
            width:         28,
            height:        28,
            borderRadius:  "50%",
            background:    "rgba(255,255,255,0.85)",
            border:        "none",
            cursor:        "pointer",
            fontSize:      13,
            fontWeight:    700,
            color:         "var(--ink)",
            display:       "flex",
            alignItems:    "center",
            justifyContent:"center",
            backdropFilter:"blur(4px)",
          }}
        >↓</button>
      </div>

      {/* Vollbild-Lightbox */}
      {expanded && (
        <div role="dialog" aria-modal="true" onClick={() => setExpanded(false)}
          style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.88)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 20, animation: "wos-rise 0.2s ease" }}>
          <img src={photo.url} alt={photo.comment || "Hochzeitsfoto"}
            style={{ maxWidth: "100%", maxHeight: "75dvh", borderRadius: "var(--radius-md)", objectFit: "contain" }}
            onClick={(e) => e.stopPropagation()} />

          {photo.comment && (
            <p style={{ marginTop: 14, color: "rgba(255,255,255,0.75)", textAlign: "center", fontStyle: "italic", fontFamily: "var(--font-display)", fontSize: "1.1rem" }}>
              {photo.comment}
            </p>
          )}

          {/* Download in Lightbox */}
          <button
            onClick={(e) => { e.stopPropagation(); handleDownload(e); }}
            style={{ marginTop: 16, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "999px", color: "#fff", padding: "10px 24px", cursor: "pointer", fontSize: 13, fontFamily: "var(--font-body)" }}
          >
            ↓ Foto herunterladen
          </button>

          <button onClick={() => setExpanded(false)} style={{ marginTop: 10, background: "none", border: "none", color: "rgba(255,255,255,0.4)", padding: "8px 24px", cursor: "pointer", fontSize: 13, fontFamily: "var(--font-body)" }}>
            Schließen
          </button>
        </div>
      )}
    </>
  );
}

// ── AlbumTab ──────────────────────────────────────────────────────────
function AlbumTab({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ padding: "6px 12px", borderRadius: "999px", border: "1px solid var(--line)", background: active ? "var(--accent)" : "var(--surface)", color: active ? "#fff" : "var(--ink-muted)", fontSize: 12, cursor: "pointer", fontFamily: "var(--font-body)", fontWeight: active ? 600 : 400, transition: "background 0.15s, color 0.15s" }}>
      {children}
    </button>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
function UploadTrigger({ camRef, galRef, onFileChange }) {
  const btn = {
    flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    gap: 6, padding: "24px 12px", background: "var(--surface)", border: "2px dashed var(--line)",
    borderRadius: "var(--radius-lg)", cursor: "pointer", transition: "border-color 0.15s ease",
  };
  const hover = (on) => (e) => e.currentTarget.style.borderColor = on ? "var(--accent)" : "var(--line)";
  return (
    <div className="wos-rise wos-rise-delay-1" style={{ marginBottom: 28 }}>
      {/* Kamera: capture erzwingt Kamera. Galerie: ohne capture = Foto-Bibliothek/Dateien */}
      <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onFileChange} style={{ display: "none" }} />
      <input ref={galRef} type="file" accept="image/*" multiple onChange={onFileChange} style={{ display: "none" }} />
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => camRef.current?.click()} style={btn} onMouseEnter={hover(true)} onMouseLeave={hover(false)}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>Kamera</span>
          <span className="wos-meta" style={{ fontSize: 12 }}>Direkt aufnehmen</span>
        </button>
        <button onClick={() => galRef.current?.click()} style={btn} onMouseEnter={hover(true)} onMouseLeave={hover(false)}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem" }}>Galerie</span>
          <span className="wos-meta" style={{ fontSize: 12 }}>Mehrere möglich</span>
        </button>
      </div>
    </div>
  );
}

// ── BatchProgress ─────────────────────────────────────────────────────
function BatchProgress({ batch }) {
  const pct = batch.total ? Math.round((batch.done / batch.total) * 100) : 0;
  return (
    <div className="wos-rise" style={{ padding: "24px 20px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", marginBottom: 28, textAlign: "center" }}>
      <p style={{ fontFamily: "var(--font-display)", fontSize: "1.2rem", margin: "0 0 12px" }}>
        Lädt {batch.done} / {batch.total} …
      </p>
      <div style={{ height: 8, borderRadius: 999, background: "var(--line)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "var(--accent)", transition: "width 0.3s ease" }} />
      </div>
      <p className="wos-meta" style={{ fontSize: 12, marginTop: 10 }}>Bitte Seite offen lassen.</p>
    </div>
  );
}

function GalerieSkeleton() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 32 }}>
      {[0,1,2,3].map((i) => <div key={i} style={{ aspectRatio: "1", borderRadius: "var(--radius-md)", background: "var(--surface)", opacity: 0.5 }} />)}
    </div>
  );
}

function SuccessBanner({ message }) {
  return (
    <div role="status" style={{ padding: "14px 18px", background: "var(--forest-soft)", border: "1px solid var(--forest)", borderRadius: "var(--radius-md)", marginBottom: 20, color: "var(--forest)", fontWeight: 500, fontSize: 15, animation: "wos-rise 0.3s ease", textAlign: "center" }}>
      {message}
    </div>
  );
}

function PageHeader({ onBack, title }) {
  return (
    <header className="wos-rise" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
      <button onClick={onBack} aria-label="Zurück" style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--ink-muted)", cursor: "pointer", flexShrink: 0 }}>←</button>
      <div>
        <p className="wos-eyebrow">Gast-Aktion</p>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: "1.7rem", marginTop: 2 }}>{title}</h1>
      </div>
    </header>
  );
}

function NoSession() {
  return (
    <div className="wos-shell" style={{ textAlign: "center", paddingTop: 80 }}>
      <p className="wos-meta">Sitzung abgelaufen. Bitte Einladungslink neu öffnen.</p>
    </div>
  );
}
